import { useEffect, useState } from 'react'

import ResponsiveImage from '~/components/Image/ResponsiveImage'

import styles from '~/bootcamp/Activities/Fractions/components/FractionNumlineSlider.module.css'

import { NumberLineSrcKeys } from '~/bootcamp/Activities/SanityActivities/NumberLine/numbers.types'
import { bootcampNumberLineActivityTick } from '~/types/bootcamp'

import { NumberLineActivityData } from './NumberLine.type'

function NumberLineSlider({
  activityData,
  danger = false,
  setTicksManually = false,
  setValue,
  value,
}: {
  activityData: NumberLineActivityData
  value: number
  danger?: boolean
  setTicksManually?: boolean
  setValue: (value: number) => void
}): React.ReactElement {
  const { leftMostValue, rightMostValue, numberOfTicks, showValueOfEveryNthTick } = activityData.question
  // Calculate increment per tick for when ticks are not set manually
  const incrementPerTick = ((rightMostValue - leftMostValue) / (numberOfTicks - 1)).toFixed(3)
  const [ticks, setTicks] = useState<bootcampNumberLineActivityTick[]>([
    { tickValue: '', large: false, isDefaultTick: false, isAnswerTick: false },
  ])

  useEffect(() => {
    if (setTicksManually) {
      setTicks(activityData.question.ticks)
    } else {
      setTicks(
        Array.from({ length: numberOfTicks }, (_, i) => ({
          tickValue: i.toString(),
          large: false,
          isDefaultTick: false,
          isAnswerTick: false,
        })),
      )
    }
  }, [activityData.question.numberOfTicks, activityData.question.ticks, numberOfTicks, setTicksManually])

  return (
    <>
      <div className="w-full flex justify-between mx-2">
        {setTicksManually
          ? ticks.map((tick, index) => (
              <div
                className="w-auto text-center text-purple font-semibold text-md sm:text-lg"
                key={index.toString() + tick.tickValue}
              >
                {tick.tickValue}
              </div>
            ))
          : ticks.map((tick, index) => {
              // Calculate the tick value based on the left most value and the increment per tick
              const tickValue = parseFloat((leftMostValue + Number(index) * Number(incrementPerTick)).toFixed(4))
              // Show the tick value if it is the first, last, or every nth tick
              const showTickValue = index % showValueOfEveryNthTick === 0 || index === 0 || index === ticks.length - 1
              return (
                <div
                  className={`w-auto text-center font-semibold text-md sm:text-lg ${
                    showTickValue ? 'text-purple' : ''
                  }`}
                  key={index.toString() + tickValue.toString()}
                >
                  {showTickValue ? tickValue : ''}
                </div>
              )
            })}
      </div>
      <div className="w-full relative mx-4">
        <div className="bg-purple h-0.5 w-full absolute top-1/2" />
        <div className="flex w-full justify-between items-center">
          {ticks.map((item, index) => {
            let isLargeTick = false
            // Check if the tick is the first, last, or middle tick (when tick length is 11), then the tick should be larger
            if (setTicksManually) {
              isLargeTick = item.large === true
            } else {
              isLargeTick = index === 0 || index === ticks.length - 1 || index % showValueOfEveryNthTick === 0
            }
            return <div className={`bg-purple h-${isLargeTick ? '8' : '4'} w-0.5`} key={index.toString() + 1} />
          })}
        </div>
      </div>
      <div className="w-full h-6 relative mx-4 mt-2">
        <ResponsiveImage
          height={31}
          imageClassName="absolute"
          imageWidths="20"
          srcKey={danger ? NumberLineSrcKeys.ARROW_UP_RED : NumberLineSrcKeys.ARROW_UP}
          style={{
            transform: 'translateX(-10px)',
            left: `${(value / (ticks.length - 1)) * 100}%`,
          }}
          width={20}
        />
      </div>
      <div className="w-full mt-2">
        <input
          className={`${styles['numline-slider']} ${danger && styles['numline-slider-danger']}`}
          max={ticks.length - 1}
          min={0}
          onChange={(e) => {
            setValue(parseInt(e.target.value))
          }}
          type="range"
          value={value}
        />
      </div>
    </>
  )
}

export default NumberLineSlider
